import React, { useEffect, useState, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Spin } from 'antd';
import * as Sentry from '@sentry/react';
import { AppLayout } from '../shared/components/AppLayout';
import { LoginPage } from '../features/auth/LoginPage';
import { useAuth } from '../features/auth/useAuth';
import { getAccessToken } from '../shared/api/client';
import { SentryFallback } from '../shared/components/SentryFallback';

const PatientsPage    = React.lazy(() => import('../features/patients/PatientsPage').then(m => ({ default: m.PatientsPage })));
const PatientPage     = React.lazy(() => import('../features/patients/PatientPage').then(m => ({ default: m.PatientPage })));
const ProsthesesPage  = React.lazy(() => import('../features/prostheses/ProsthesesPage').then(m => ({ default: m.ProsthesesPage })));
const ExercisesPage   = React.lazy(() => import('../features/exercises/ExercisesPage').then(m => ({ default: m.ExercisesPage })));
const MedicationsPage = React.lazy(() => import('../features/medications/MedicationsPage').then(m => ({ default: m.MedicationsPage })));
const LfkProgramsPage = React.lazy(() => import('../features/lfk/LfkProgramsPage').then(m => ({ default: m.LfkProgramsPage })));
const LfkProgramPage  = React.lazy(() => import('../features/lfk/LfkProgramPage').then(m => ({ default: m.LfkProgramPage })));
const PainProgramsPage = React.lazy(() => import('../features/pain/PainProgramsPage').then(m => ({ default: m.PainProgramsPage })));
const PainProgramPage  = React.lazy(() => import('../features/pain/PainProgramPage').then(m => ({ default: m.PainProgramPage })));
const SurveysPage     = React.lazy(() => import('../features/surveys/SurveysPage').then(m => ({ default: m.SurveysPage })));
const SurveyPage      = React.lazy(() => import('../features/surveys/SurveyPage').then(m => ({ default: m.SurveyPage })));
const CheckpointsPage = React.lazy(() => import('../features/checkpoints/CheckpointsPage').then(m => ({ default: m.CheckpointsPage })));
const MatrixPage      = React.lazy(() => import('../features/matrix/MatrixPage').then(m => ({ default: m.MatrixPage })));
const TodayTasksPage  = React.lazy(() => import('../features/today-tasks/TodayTasksPage').then(m => ({ default: m.TodayTasksPage })));
const SchedulerPage   = React.lazy(() => import('../features/scheduler/SchedulerPage').then(m => ({ default: m.SchedulerPage })));
const OperationsPage  = React.lazy(() => import('../features/operations/OperationsPage').then(m => ({ default: m.OperationsPage })));
const DiaryTriggersPage = React.lazy(() => import('../features/diary-triggers/DiaryTriggersPage').then(m => ({ default: m.DiaryTriggersPage })));
const NotificationSettingsPage = React.lazy(() => import('../features/settings/NotificationSettingsPage').then(m => ({ default: m.NotificationSettingsPage })));
const GeneralSettingsPage = React.lazy(() => import('../features/settings/GeneralSettingsPage').then(m => ({ default: m.GeneralSettingsPage })));

function ProtectedRoute() {
  const { initAuth } = useAuth();
  const [checking, setChecking] = useState(!getAccessToken());

  useEffect(() => {
    if (!getAccessToken()) {
      initAuth().finally(() => setChecking(false));
    }
  }, [initAuth]);

  if (checking) return <Spin fullscreen />;
  if (!getAccessToken()) return <Navigate to="/login" replace />;

  return (
    <Suspense fallback={<Spin fullscreen />}>
      <AppLayout />
    </Suspense>
  );
}

export function App() {
  return (
    <Sentry.ErrorBoundary fallback={({ resetError }) => <SentryFallback resetError={resetError} />}>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route element={<ProtectedRoute />}>
            <Route path="/patients"           element={<PatientsPage />} />
            <Route path="/patients/:id"       element={<PatientPage />} />
            <Route path="/prostheses"         element={<ProsthesesPage />} />
            <Route path="/exercises"          element={<ExercisesPage />} />
            <Route path="/medications"        element={<MedicationsPage />} />
            <Route path="/lfk-programs"       element={<LfkProgramsPage />} />
            <Route path="/lfk-programs/:id"   element={<LfkProgramPage />} />
            <Route path="/pain-programs"      element={<PainProgramsPage />} />
            <Route path="/pain-programs/:id"  element={<PainProgramPage />} />
            <Route path="/surveys"            element={<SurveysPage />} />
            <Route path="/surveys/:id"        element={<SurveyPage />} />
            <Route path="/checkpoints"        element={<CheckpointsPage />} />
            <Route path="/survey-matrix"      element={<MatrixPage />} />
            <Route path="/today-tasks"        element={<TodayTasksPage />} />
            <Route path="/scheduler"          element={<SchedulerPage />} />
            <Route path="/operations"         element={<OperationsPage />} />
            <Route path="/diary-triggers"     element={<DiaryTriggersPage />} />
            <Route path="/settings/notifications" element={<NotificationSettingsPage />} />
            <Route path="/settings/general" element={<GeneralSettingsPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/patients" replace />} />
        </Routes>
      </BrowserRouter>
    </Sentry.ErrorBoundary>
  );
}
